import { fragSampler } from '../shaders/frag-sampler.shader';
import { vertSampler } from '../shaders/vert-sampler.shader';

import { RegionMask } from './components/regionMasks/regionMask';
import { BasePipeline, BaseUniformData, ShaderPropBufferData } from './pipeline-base';
import { PipelineGlUtils } from './utils/pipeline-utils-gl';

const VERT_TEXTURE_BUFFER_NAME = 'a_texCoord';

export class TexturePipelineBase extends BasePipeline {
  public glTextures: WebGLTexture[];
  protected renderingContextBase: number;
  private flipYValue: number;

  // ! Here to be recognized by all classes of this type, even though it makes no sense to be on this class directly... (would never be used at this level)
  // TODO: I need to figure out better modularity for this whole pipeline system, including whether or not textures are even used.
  public regionMask?: RegionMask;

  public constructor(vertShader?: string, fragShader?: string) {
    super(vertShader || vertSampler, fragShader || fragSampler);

    this.glTextures = [];
    this.renderingContextBase = 0;
    this.flipYValue = 1;
  }

  public get HasTexture(): boolean {
    return this.glTextures.length > 0;
  }

  public get FlipYValue(): number {
    return this.flipYValue;
  }

  public get TransformMatrix(): number[] {
    return [
      1, 0, 0, 0,
      0, this.FlipYValue, 0, 0,
      0, 0, 1, 0,
      0, 0, 0, 1
    ];
  }

  public SetFlipY(flipped: boolean) {
    this.flipYValue = flipped ? -1 : 1;
  }

  protected OnContextAcquired(): void {
    this.renderingContextBase = this.gl.CLAMP_TO_EDGE;
  }

  public CreateTextures(imgDataList: ImageData[]): WebGLTexture[] {
    if (imgDataList.length <= 0) {
      throw new Error(`TexturePipelineBase::CreateTextures called with an empty texture list`);
    }
    // console.log(`TexturePipelineBase::CreateTextures: Creating webGL textures from image data list:`, imgDataList);

    const w = imgDataList[0].width;
    const h = imgDataList[0].height;
    for (let i = 0; i < imgDataList.length; i++) {
      const data = imgDataList[i];
      if (data.width !== w || data.height !== h) {
        throw new Error(`CreateTextures called with multiple textures of different dimensions: ` +
          `index 0, w:<${w}>, h:<${h}>, index ${i}, width:<${data.width}>, height:<${data.height}>. ` +
          `Cannot operate within a single shader in this manner.`);
      }
    }

    // ! Cannot necessarily equate canvas sizing with texture sizing (tiling shader). Must do explicit canvas size-setting seperately.
    // this.SetDimensions(w, h);

    const textures: WebGLTexture[] = [];
    for (const data of imgDataList) {
      const texture = PipelineGlUtils.CreateGlTextureByImageData(this.gl, data, this.renderingContextBase);
      textures.push(texture);
    }

    this.SetGlTextures(textures);
    return textures;
  }

  public SetGlTextures(textures: WebGLTexture[]) {
    // console.log(`TexturePipeline::SetGlTextures: Setting new texture array:`, textures);
    this.glTextures = textures;
  }

  protected InitShaderProps(shaderProgram: WebGLProgram) {
    super.InitShaderProps(shaderProgram);

    this.shaderProgramProps[VERT_TEXTURE_BUFFER_NAME] = PipelineGlUtils.EnableVertexAttributeArray(this.gl, shaderProgram, 'inTexCoord');
    this.shaderProgramProps.u_TransformMatrix = PipelineGlUtils.GetUniformLocation(this.gl, shaderProgram, 'uTransformMatrix');
    this.shaderProgramProps.u_samplers = PipelineGlUtils.GetUniformLocation(this.gl, shaderProgram, 'uSamplers[0]');
  }

  protected InitBuffers() {
    super.InitBuffers();

    // TEXTURE INDICES
    const texCoords = this.GetTexCoords();
    this.InitTexCoordBuffer(texCoords);
  }

  protected GetTexCoords(): number[] {
    return [
      0.0, 0.0, // TL
      0.0, 1.0, // BL
      1.0, 1.0, // BR
      1.0, 0.0 // TR
    ];
  }

  protected InitTexCoordBuffer(texCoords: number[]) {
    this.buffers[VERT_TEXTURE_BUFFER_NAME] = {
      buffer: this.gl.createBuffer(),
      itemSize: 2,
      itemCount: texCoords.length / 2
    };
    const texBufferData: ShaderPropBufferData = this.buffers[VERT_TEXTURE_BUFFER_NAME];
    if (!texBufferData || texBufferData.buffer === null) {
      throw new Error(`${VERT_TEXTURE_BUFFER_NAME} does not exist!`);
    }
    const texBuffer: any = texBufferData.buffer;
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, texBuffer);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array(texCoords), this.gl.STATIC_DRAW);

    // console.log(`Texture coord buffer added, this.buffers:`, this.buffers);
  }

  protected SetVertexAttributes() {
    if (!this.gl) { throw new Error('base does not have gl.'); }
    super.SetVertexAttributes();
    this.SetVertexAttribute(VERT_TEXTURE_BUFFER_NAME);
  }

  protected SetUniforms(data: BaseUniformData) {
    super.SetUniforms(data);

    this.gl.uniformMatrix4fv(this.shaderProgramProps.u_TransformMatrix, false, this.TransformMatrix);

    // * This will ensure that it's always in it's default state, and flip when told to, ahead of each given draw call.
    this.SetFlipY(false);

    for (let i = 0; i < this.glTextures.length; i++) {
      // console.log(`TexturePipeline::SetUniforms: Setting texture <${i}> uniform of <${this.glTextures.length}> total, texture:`, this.glTextures[i]);

      const texture = this.glTextures[i];
      PipelineGlUtils.SetTextureUniform(this.gl, this.gl.TEXTURE0 + i, texture, this.shaderProgramProps.u_samplers, i);
    }
  }

  public Draw(uniformData: BaseUniformData): void {
    if (!this.HasTexture) {
      throw new Error(`TexturePipeline::Draw called with <${this.glTextures.length}> textures cached to draw from`);
    }
    super.Draw(uniformData);
  }

  public DrawToTexture(uniformData: BaseUniformData): WebGLTexture {
    if (!this.gl) {
      // eslint-disable-next-line no-console
      console.trace('pipeline-textBase DrawToTexture error');
      throw new Error(`Expected this.gl to be defined, but it was ${this.gl}`);
    }
    this.SetFlipY(true);
    return super.DrawToTexture(uniformData);
  }

  public DrawOnceForPixelData(uniformData: BaseUniformData = {}): Uint8Array {
    if (this.glTextures.length === 0) {
      throw new Error(`TexturePipelineBase::DrawOnceForPixelData: No textures cached to get pixels of. At least 1 required here`);
    }

    return super.DrawOnceForPixelData(uniformData);
  }

  public CleanGlState() {
    super.CleanGlState();

    const numTextureUnits = this.gl.getParameter(this.gl.MAX_TEXTURE_IMAGE_UNITS);
    for (let unit = 0; unit < numTextureUnits; unit++) {
      this.gl.activeTexture(this.gl.TEXTURE0 + unit);
      this.gl.bindTexture(this.gl.TEXTURE_2D, null);
      this.gl.bindTexture(this.gl.TEXTURE_CUBE_MAP, null);
    }

    // I don't think I have anything here that needs specific removal
    // this.gl.deleteTexture();
    // this.gl.deleteRenderbuffer();
    // this.gl.deleteFramebuffer();

    this.glTextures = [];
  }
}
